import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  Fingerprint,
  KeyRound,
  Lock,
  MonitorSmartphone,
  ShieldAlert,
  Siren,
  Sparkles,
  Timer,
  TriangleAlert,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useT, useI18n, LANG_LABELS, type Lang } from '@/i18n'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/providers/AuthProvider'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { PlannedBadge } from '@/components/DataDisplay'
import { detectEnvironment } from '@/shell/AppShell'

/**
 * A1 — Autentifikatsiya va kirish (8 ekran).
 *
 * ⚠️ TZ A1.01/A1.02 SSO (`my.casinoplaneta.info`) + MAJBURIY WebAuthn deydi.
 * Ikkalasining ham provayderi yo'q (PROGRESS.md §4.2: «Admin SSO/WebAuthn —
 * provayder yo'q»). Jonli yadro faqat `POST /admin/login` (login+parol → admin
 * JWT, 8 soat) beradi.
 *
 * QAROR (foydalanuvchi bilan kelishilgan): mavjud JWT oqimi ustiga quriladi,
 * SSO/WebAuthn/break-glass ekranlari OCHIQ «rejalashtirilgan» belgisi bilan
 * ko'rsatiladi — ishlamaydigan narsa ishlaydi deb ko'rsatilmaydi.
 */

/* ── Umumiy karkas ────────────────────────────────────────────── */

function AuthLayout({
  icon,
  title,
  description,
  children,
  screenId,
  tone = 'neutral',
}: {
  icon: ReactNode
  title: string
  description?: string
  children: ReactNode
  screenId: string
  tone?: 'neutral' | 'danger' | 'warning'
}) {
  const { lang, setLang } = useI18n()
  const env = detectEnvironment()

  const ring =
    tone === 'danger' ? 'border-danger' : tone === 'warning' ? 'border-warning' : 'border-border-default'

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-0 p-6">
      {env !== 'prod' && (
        <div
          className="mb-4 rounded-sm px-2 py-0.5 text-xs font-semibold text-black uppercase"
          style={{ background: env === 'staging' ? 'var(--env-staging)' : 'var(--env-dev)' }}
        >
          {env}
        </div>
      )}

      <div
        className={cn('w-full max-w-[380px] rounded-lg border bg-surface-2 p-5', ring)}
        style={{ boxShadow: 'var(--shadow-panel)' }}
        data-screen={screenId}
      >
        <div className="mb-4 flex flex-col items-center gap-2 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-3">{icon}</div>
          <div>
            <h1 className="text-lg font-semibold text-primary">{title}</h1>
            {description && <p className="mt-0.5 text-sm text-secondary">{description}</p>}
          </div>
        </div>
        {children}
      </div>

      <div className="mt-4 flex items-center gap-1">
        {(Object.keys(LANG_LABELS) as Lang[]).map((l) => (
          <button
            key={l}
            type="button"
            onClick={() => setLang(l)}
            className={cn(
              'rounded-sm px-2 py-0.5 text-xs transition-colors',
              lang === l ? 'text-primary' : 'text-tertiary hover:text-secondary',
            )}
          >
            {LANG_LABELS[l]}
          </button>
        ))}
        <span className="ml-2 font-mono text-xs text-disabled">{screenId}</span>
      </div>
    </div>
  )
}

/* ── A1.01 Kirish ─────────────────────────────────────────────── */

export function LoginScreen() {
  const t = useT()
  const { signIn } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [needTotp, setNeedTotp] = useState(false) // A28.02 — server asked for a 2FA code
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const userRef = useRef<HTMLInputElement>(null)
  const passRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    userRef.current?.focus()
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    // MUHIM: qiymatlar DOM'dan o'qiladi, React holatidan emas.
    // Chrome/parol-menejeri avto-to'ldirganda `onChange` ISHGA TUSHMAYDI —
    // holat bo'sh qoladi va admin parol menejeri bilan kira olmaydi.
    // (Brauzerda sinab topilgan nuqson.)
    const u = (userRef.current?.value ?? username).trim()
    const p = passRef.current?.value ?? password
    if (!u || !p) {
      setError(t.auth.invalidCredentials)
      return
    }

    setBusy(true)
    setError(null)
    try {
      await signIn(u, p, needTotp ? totp.trim() : undefined)
    } catch (err) {
      // A28.02 — parol to'g'ri, lekin 2FA kodi kerak/xato: kod maydonini ko'rsatamiz.
      if (err instanceof ApiError && err.code === 'TWO_FACTOR_REQUIRED') {
        setNeedTotp(true)
        setError(needTotp ? t.auth.twoFactorInvalid : null)
        setTotp('')
      } else if (err instanceof ApiError && err.code === 'NETWORK') setError(t.auth.networkError)
      else if (err instanceof ApiError && err.status === 403) setError(t.auth.ipDeniedTitle)
      else {
        setError(t.auth.invalidCredentials)
        setPassword('')
        setNeedTotp(false)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      screenId="A1.01"
      icon={<KeyRound size={20} className="text-accent" />}
      title={t.auth.signInTitle}
      description={t.auth.signInSubtitle}
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <Field label={t.auth.username} htmlFor="u" required>
          <Input
            id="u"
            ref={userRef}
            value={username}
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value)}
          />
        </Field>

        <Field label={t.auth.password} htmlFor="p" required>
          <Input
            id="p"
            ref={passRef}
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        {needTotp && (
          <Field label={t.auth.twoFactorCode} htmlFor="totp" required>
            <Input
              id="totp"
              value={totp}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              autoFocus
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))}
            />
          </Field>
        )}

        {error && (
          <p className="flex items-center gap-1.5 rounded-md border border-danger bg-danger-subtle px-2 py-1.5 text-sm text-danger">
            <TriangleAlert size={13} className="shrink-0" />
            {error}
          </p>
        )}

        {/* Faqat `busy` bo'yicha o'chiriladi — bo'shlik tekshiruvi `onSubmit` da,
            aks holda avto-to'ldirilgan forma yuborib bo'lmaydi. */}
        <Button type="submit" variant="primary" size="lg" disabled={busy} className="w-full justify-center">
          {busy ? t.auth.signingIn : t.auth.signIn}
        </Button>
      </form>

      {/* A1.01 SSO + A1.02 WebAuthn — provayder yo'q, ochiq belgilanadi */}
      <div className="mt-4 space-y-2 border-t border-border-subtle pt-3">
        <PlannedRow icon={<Fingerprint size={13} />} title={t.auth.webauthnTitle} desc={t.auth.webauthnDesc} id="A1.02" />
        <PlannedRow icon={<KeyRound size={13} />} title={t.auth.ssoTitle} desc={t.auth.ssoDesc} id="A1.01" />
      </div>
    </AuthLayout>
  )
}

function PlannedRow({
  icon,
  title,
  desc,
  id,
}: {
  icon: ReactNode
  title: string
  desc: string
  id: string
}) {
  return (
    <div className="flex gap-2 rounded-md border border-dashed border-border-default px-2.5 py-2">
      <span className="mt-0.5 shrink-0 text-tertiary">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-secondary">{title}</span>
          <PlannedBadge />
          <span className="font-mono text-xs text-disabled">{id}</span>
        </div>
        <p className="mt-0.5 text-xs text-tertiary">{desc}</p>
      </div>
    </div>
  )
}

/* ── A1.05 Sessiya muddati tugadi ─────────────────────────────── */

export function SessionExpiredScreen({ onContinue }: { onContinue: () => void }) {
  const t = useT()
  return (
    <AuthLayout
      screenId="A1.05"
      tone="warning"
      icon={<Timer size={20} className="text-warning" />}
      title={t.auth.sessionExpiredTitle}
      description={t.auth.sessionExpiredDesc}
    >
      <Button variant="primary" size="lg" className="w-full justify-center" onClick={onContinue}>
        {t.auth.sessionExpiredAction}
      </Button>
    </AuthLayout>
  )
}

/* ── Qulflangan (30 daqiqa faoliyatsizlik, TZ A1) ─────────────── */

export function LockScreen() {
  const t = useT()
  const { identity, unlock, signOut } = useAuth()
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    // LoginScreen dagi kabi — avto-to'ldirish React holatini yangilamaydi.
    const p = ref.current?.value ?? password
    if (!p) return
    setBusy(true)
    setError(null)
    try {
      await unlock(p)
    } catch {
      setError(t.auth.invalidCredentials)
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      screenId="A1"
      icon={<Lock size={20} className="text-warning" />}
      title={t.auth.idleLockedTitle}
      description={t.auth.idleLockedDesc}
    >
      <form onSubmit={onSubmit} className="space-y-3">
        <p className="text-center text-sm text-secondary">{identity?.username}</p>
        <Field label={t.auth.password} htmlFor="lock-p">
          <Input
            id="lock-p"
            ref={ref}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button type="submit" variant="primary" size="lg" className="w-full justify-center" disabled={busy}>
          {t.auth.unlock}
        </Button>
        <Button variant="ghost" size="sm" className="w-full justify-center" onClick={signOut}>
          {t.common.signOut}
        </Button>
      </form>
    </AuthLayout>
  )
}

/* ── A1.03 Qurilmani ro'yxatdan o'tkazish ─────────────────────────
   Server yo'q: yadro qurilma tekshiruvi/tasdiqlash oqimini bilmaydi
   (POST /admin/login `new_device` qaytarmaydi). Shu sabab bu ekran
   «kutish» holatida turadi va PLANNED belgilanadi — soxta «tasdiqlandi»
   holatiga o'tmaydi. Server kelganda `AuthGate` ga `device_pending`
   holati orqali ulanadi. tz/admin/HOLAT.md §2-P3. */

export function DeviceRegisterScreen({ fingerprint }: { fingerprint?: string }) {
  const t = useT()
  return (
    <AuthLayout
      screenId="A1.03"
      icon={<MonitorSmartphone size={20} className="text-accent" />}
      title={t.auth.deviceTitle}
      description={t.auth.deviceDesc}
    >
      <div className="space-y-3">
        <div className="rounded-md border border-border-default bg-surface-1 p-2.5">
          <p className="text-xs text-tertiary">{t.auth.deviceFingerprint}</p>
          <p className="mt-0.5 font-mono text-sm break-all text-secondary">
            {fingerprint ?? '—'}
          </p>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-warning bg-warning-subtle p-2.5">
          <Timer size={14} className="mt-0.5 shrink-0 text-warning" />
          <div>
            <p className="text-sm font-medium text-warning">{t.auth.devicePending}</p>
            <p className="mt-0.5 text-sm text-secondary">{t.auth.deviceWaiting}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 rounded-md border border-dashed border-border-default p-2.5">
          <PlannedBadge />
          <span className="font-mono text-xs text-tertiary">POST /admin/devices/register</span>
        </div>
      </div>
    </AuthLayout>
  )
}

/* ── A1.04 Favqulodda kirish (break-glass) ────────────────────────
   Server yo'q: TZ A1.04 (super_admin 4 soatga barcha huquq + jamoaga
   xabar + har amal alohida audit) uchun endpoint yozilmagan. Bu ekran
   xavflilikni TO'LIQ ko'rsatadi (sabab majburiy, ogohlantirish), lekin
   faollashtirish tugmasi PLANNED — sinalmagan favqulodda-kirish oqimini
   jonli qilish xavfsizlik uchun yo'qidan yomon. tz/admin/HOLAT.md §2-P3. */

export function BreakGlassScreen() {
  const t = useT()
  const [reason, setReason] = useState('')

  return (
    <AuthLayout
      screenId="A1.04"
      tone="danger"
      icon={<Siren size={20} className="text-danger" />}
      title={t.auth.breakGlassTitle}
      description={t.auth.breakGlassDesc}
    >
      <div className="space-y-3">
        <div className="flex items-start gap-2 rounded-md border border-danger bg-danger-subtle p-2.5">
          <TriangleAlert size={14} className="mt-0.5 shrink-0 text-danger" />
          <p className="text-sm text-secondary">{t.auth.breakGlassWarning}</p>
        </div>

        <Field label={t.auth.breakGlassReason} htmlFor="bg-reason" required>
          <textarea
            id="bg-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t.auth.breakGlassReasonHint}
            className={cn(
              'w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-base text-primary',
              'placeholder:text-tertiary focus:border-accent focus:outline-none',
            )}
          />
        </Field>

        {/* Faollashtirish tugmasi ATAYLAB o'chiq — endpoint yo'q. */}
        <Button variant="danger" size="lg" className="w-full justify-center" disabled>
          {t.auth.breakGlassActivate}
        </Button>
        <div className="flex items-center gap-2 rounded-md border border-dashed border-border-default p-2.5">
          <PlannedBadge />
          <span className="font-mono text-xs text-tertiary">POST /admin/break-glass</span>
        </div>
      </div>
    </AuthLayout>
  )
}

/* ── A1.06 IP cheklovi ────────────────────────────────────────── */

export function IpDeniedScreen({ ip }: { ip?: string }) {
  const t = useT()
  return (
    <AuthLayout
      screenId="A1.06"
      tone="danger"
      icon={<ShieldAlert size={20} className="text-danger" />}
      title={t.auth.ipDeniedTitle}
      description={t.auth.ipDeniedDesc}
    >
      <div className="space-y-3">
        {ip && (
          <div className="rounded-md border border-border-default bg-surface-1 p-2.5">
            <p className="text-xs text-tertiary">{t.auth.ipCurrent}</p>
            <p className="mt-0.5 font-mono text-sm text-secondary">{ip}</p>
          </div>
        )}
        <div className="flex items-center gap-2 rounded-md border border-dashed border-border-default p-2.5">
          <PlannedBadge />
          <span className="font-mono text-xs text-tertiary">IP allowlist</span>
        </div>
      </div>
    </AuthLayout>
  )
}

/* ── A1.08 Xush kelibsiz / onboarding ─────────────────────────────
   Birinchi MUVAFFAQIYATLI kirishdan keyin bir marta ko'rinadi (har
   username uchun, klient-bayroq — lib/auth.ts). Server-oqim talab
   qilmaydi: sof tanishtirish qadami. «Boshlash» bosilgach bayroq
   yoziladi va ekran bir daha ko'rinmaydi. */

export function OnboardingScreen({
  username,
  role,
  onDone,
}: {
  username: string
  role: string
  onDone: () => void
}) {
  const t = useT()
  const steps = [t.auth.welcomeStepRole, t.auth.welcomeStepSecurity, t.auth.welcomeStepTour]
  return (
    <AuthLayout
      screenId="A1.08"
      icon={<Sparkles size={20} className="text-accent" />}
      title={`${t.auth.welcomeTitle}, ${username}`}
      description={t.auth.welcomeDesc}
    >
      <div className="space-y-3">
        <ol className="space-y-1.5">
          {steps.map((s, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 rounded-md border border-border-default bg-surface-1 px-2.5 py-2"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-subtle font-mono text-xs text-accent">
                {i + 1}
              </span>
              <span className="text-sm text-secondary">{s}</span>
            </li>
          ))}
        </ol>
        <p className="text-center text-xs text-tertiary">
          {t.auth.welcomeRole}: <span className="font-mono text-secondary">{role}</span>
        </p>
        <Button variant="primary" size="lg" className="w-full justify-center" onClick={onDone}>
          {t.auth.welcomeAction}
        </Button>
      </div>
    </AuthLayout>
  )
}

/* ── A1.07 Rol tanlash ────────────────────────────────────────── */

export function RoleSelectScreen({
  roles,
  onSelect,
}: {
  roles: string[]
  onSelect: (role: string) => void
}) {
  const t = useT()
  return (
    <AuthLayout
      screenId="A1.07"
      icon={<KeyRound size={20} className="text-accent" />}
      title={t.auth.roleSelectTitle}
      description={t.auth.roleSelectDesc}
    >
      <ul className="space-y-1.5">
        {roles.map((r) => (
          <li key={r}>
            <button
              type="button"
              onClick={() => onSelect(r)}
              className="w-full rounded-md border border-border-default px-3 py-2 text-left text-base text-primary hover:border-accent hover:bg-accent-subtle"
            >
              <span className="font-mono text-sm">{r}</span>
            </button>
          </li>
        ))}
      </ul>
    </AuthLayout>
  )
}
